import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// Import new tab components
import DevelopmentPipelineTab from "./DevelopmentPipelineTab";
import ManagerEnablementTab from "./ManagerEnablementTab";
import ComplianceStatusTab from "./ComplianceStatusTab";
import ProgramsInvestmentTab from "./ProgramsInvestmentTab";
import ProjectTrackerTab from "../shared/ProjectTrackerTab";
import { 
  Loader2, 
  Search,
  Bell,
  Download,
  RefreshCw,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
  X,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  Presentation,
  Mail,
  Target,
  Zap,
  Shield,
  UserCheck,
  BarChart3,
  Lock,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Trophy,
  ScanLine
} from "lucide-react";
import CommunicationHub from "../chro/CommunicationHub";
import WorkforcePlanningTab from "./WorkforcePlanningTab";
import TransformationTab from "./TransformationTab";
import DeliverablesHubTab from "./DeliverablesHubTab";
import TechnicalAssessmentTab from "./TechnicalAssessmentTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: "all", label: "All Regions" },
  { value: "usa", label: "USA" },
  { value: "canada", label: "Canada" },
  { value: "jamaica", label: "Jamaica" },
  { value: "barbados", label: "Barbados" },
  { value: "trinidad_tobago", label: "Trinidad & Tobago" },
  { value: "panama", label: "Panama" }
];

const ASSESSMENT_TYPES = [
  { value: "all", label: "All Assessment Types" },
  { value: "self_assessment", label: "Self Assessment" },
  { value: "technical", label: "Technical Assessment" },
  { value: "manager_validation", label: "Manager Validation" },
  { value: "simulation", label: "Hands-On Simulation" }
];

const SEGMENTS = [
  { value: "all", label: "All Segments" },
  { value: "Core Builder", label: "Core Builder" },
  { value: "Emerging Builder", label: "Emerging Builder" },
  { value: "Support Developer", label: "Support Developer" },
  { value: "Transition Candidate", label: "Transition Candidate" }
];

const REGION_COLORS = {
  usa: '#3b82f6',
  canada: '#10b981',
  jamaica: '#1a365d',
  barbados: '#f59e0b',
  trinidad_tobago: '#7c3aed',
  panama: '#ef4444'
};

const SEGMENT_COLORS = {
  "Core Builder": { bg: '#1B5E20', text: 'white' },
  "Emerging Builder": { bg: '#1565C0', text: 'white' },
  "Support Developer": { bg: '#F9A825', text: '#1a1a1a' },
  "Transition Candidate": { bg: '#C62828', text: 'white' }
};

// Mini Sparkline Component
const Sparkline = ({ data, color = "#3b82f6", height = 24 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const width = 60;
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Segment Badge Component
const SegmentBadge = ({ segment }) => {
  if (!segment) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = SEGMENT_COLORS[segment] || { bg: '#94a3b8', text: 'white' };
  return (
    <span 
      className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {segment}
    </span>
  );
};

// Action Badge Component
const ActionBadge = ({ action }) => {
  if (!action) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = {
    "Retain": "bg-emerald-100 text-emerald-700",
    "Develop": "bg-blue-100 text-blue-700",
    "Reassign": "bg-amber-100 text-amber-700",
    "Transition": "bg-red-100 text-red-700"
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[action] || 'bg-slate-100 text-slate-700'}`}>
      {action}
    </span>
  );
};

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [briSummary, setBriSummary] = useState(null);
  const [segmentDistribution, setSegmentDistribution] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeesTotal, setEmployeesTotal] = useState(0);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  
  // Filters
  const [regionFilter, setRegionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [segmentFilter, setSegmentFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination & Sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const itemsPerPage = 25;
  
  // Last updated
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    const token = localStorage.getItem("executiveToken");
    if (!token) {
      navigate("/executive");
      return;
    }
    fetchAllData();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [navigate, regionFilter, typeFilter]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken")}` }
  });

  const fetchAllData = async () => {
    try {
      const params = new URLSearchParams();
      if (regionFilter !== "all") params.append("region", regionFilter);
      if (typeFilter !== "all") params.append("assessment_type", typeFilter);
      
      // Fetch main dashboard stats (includes embedded BRI, segment, and trend data)
      const [statsRes, activityRes, alertsRes] = await Promise.all([
        axios.get(`${API}/executive/dashboard-stats?${params}`, getAuthHeaders()),
        axios.get(`${API}/executive/live-activity`, getAuthHeaders()),
        axios.get(`${API}/executive/alerts`, getAuthHeaders())
      ]);
      
      // Main stats
      setStats(statsRes.data);
      
      // Extract embedded BRI data from dashboard-stats response
      setBriSummary(statsRes.data.briSummary || null);
      setSegmentDistribution(statsRes.data.segmentDistribution || null);
      setWeeklyTrend(statsRes.data.weeklyTrend || null);
      
      setActivity(activityRes.data.activity);
      setAlerts(alertsRes.data.alerts);
      setLastUpdated(new Date());
      
      // Fetch employees separately
      await fetchEmployees();
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("executiveToken");
        navigate("/executive");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (regionFilter !== "all") params.append("region", regionFilter);
      if (typeFilter !== "all") params.append("assessment_type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      params.append("limit", itemsPerPage.toString());
      params.append("offset", ((currentPage - 1) * itemsPerPage).toString());
      
      // Use existing /employees endpoint which now includes BRI data
      const response = await axios.get(`${API}/executive/employees?${params}`, getAuthHeaders());
      
      // Filter by segment client-side if specified (since segment is computed)
      let employeeList = response.data.employees;
      if (segmentFilter !== "all") {
        employeeList = employeeList.filter(e => e.segment === segmentFilter);
      }
      
      setEmployees(employeeList);
      setEmployeesTotal(response.data.total);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, [regionFilter, typeFilter, statusFilter, segmentFilter, searchTerm, currentPage]);

  useEffect(() => {
    if (!loading) {
      fetchEmployees();
    }
  }, [statusFilter, segmentFilter, searchTerm, sortBy, sortOrder, currentPage, fetchEmployees, loading]);

  const dismissAlert = (index) => {
    setDismissedAlerts([...dismissedAlerts, index]);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUpRight className="w-3 h-3 text-emerald-500" />;
    if (current < previous) return <ArrowDownRight className="w-3 h-3 text-red-500" />;
    return <Minus className="w-3 h-3 text-slate-400" />;
  };

  const getTrendColor = (current, previous, inverse = false) => {
    if (current === previous) return "text-slate-500";
    if (inverse) {
      return current > previous ? "text-red-600" : "text-emerald-600";
    }
    return current > previous ? "text-emerald-600" : "text-red-600";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF] mx-auto mb-3" />
          <p className="text-[#6b7280] text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const visibleAlerts = alerts.filter((_, i) => !dismissedAlerts.includes(i));
  const totalPages = Math.ceil(employeesTotal / itemsPerPage);
  const notStartedPercentage = stats?.total > 0 ? Math.round((stats.pending / stats.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header - Clean white with Sagicor accents */}
      <header className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#63C465] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">S</span>
              </div>
              <span className="text-lg font-semibold text-[#3b3b3b] hidden sm:inline">
                Workforce Intelligence
              </span>
            </div>

            {/* Center - Search */}
            <div className="flex-1 max-w-md hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
                <Input
                  placeholder="Search by name, role, region..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10 bg-[#f8f9fa] border-[#e5e7eb] text-[#3b3b3b] placeholder:text-[#9ca3af] focus:border-[#00AEEF] h-9"
                  data-testid="exec-search"
                />
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[130px] bg-white border-[#e5e7eb] text-[#4b5563] text-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[150px] bg-white border-[#e5e7eb] text-[#4b5563] text-sm h-9 hidden lg:flex">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSESSMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="ghost" size="icon" className="text-[#4b5563] hover:text-[#3b3b3b] hover:bg-[#f3f4f6] relative h-9 w-9">
                <Bell className="w-5 h-5" />
                {visibleAlerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#ef4444] rounded-full text-[10px] text-white flex items-center justify-center">
                    {visibleAlerts.length}
                  </span>
                )}
              </Button>

              <Select defaultValue="export">
                <SelectTrigger className="w-[100px] bg-white border-[#e5e7eb] text-[#4b5563] text-sm h-9">
                  <Download className="w-4 h-4 mr-1" />
                  <span>Export</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="export" disabled>Export Options</SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Summary PDF
                    </div>
                  </SelectItem>
                  <SelectItem value="ppt">
                    <div className="flex items-center gap-2">
                      <Presentation className="w-4 h-4" />
                      PowerPoint
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <div className="w-9 h-9 bg-[#00AEEF] rounded-full flex items-center justify-center text-sm font-semibold text-white">
                NF
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs - Clean design with Sagicor Blue active state */}
      <div className="bg-white border-b border-[#e5e7eb]">
        <div className="max-w-[1600px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-12 bg-transparent border-0 gap-1 w-full justify-start">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-overview"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="technical-assessment"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-technical-assessment"
              >
                Technical Assessment
              </TabsTrigger>
              <TabsTrigger 
                value="talent-intelligence"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-talent-intelligence"
              >
                Talent Intelligence
              </TabsTrigger>
              <Link to="/executive/xray-cohort">
                <div
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-1.5 text-[13px] font-medium text-[#4b5563] hover:bg-[#00AEEF]/10 hover:text-[#00AEEF] transition-colors cursor-pointer"
                  data-testid="tab-xray-cohort-link"
                >
                  <ScanLine className="w-4 h-4 mr-1.5" />
                  X-Ray Cohort
                </div>
              </Link>
              <Link to="/executive/analytics">
                <div 
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-1.5 text-[13px] font-medium text-[#4b5563] hover:bg-[#00AEEF]/10 hover:text-[#00AEEF] transition-colors cursor-pointer"
                  data-testid="tab-analytics-link"
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  Analytics
                </div>
              </Link>
              <TabsTrigger 
                value="profiles"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-profiles"
              >
                Profiles
              </TabsTrigger>
              <TabsTrigger 
                value="workforce-planning"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-workforce-planning"
              >
                Workforce Planning
              </TabsTrigger>
              <TabsTrigger 
                value="transformation"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-transformation"
              >
                Transformation
              </TabsTrigger>
              <TabsTrigger 
                value="communications"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-communications"
              >
                Communication
              </TabsTrigger>
              <TabsTrigger 
                value="deliverables"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-deliverables"
              >
                Deliverables
              </TabsTrigger>
              <TabsTrigger 
                value="development-pipeline"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-development-pipeline"
              >
                Dev Pipeline
              </TabsTrigger>
              <TabsTrigger 
                value="manager-enablement"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-manager-enablement"
              >
                Managers
              </TabsTrigger>
              <TabsTrigger 
                value="compliance"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-compliance"
              >
                Compliance
              </TabsTrigger>
              <TabsTrigger 
                value="programs"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-programs"
              >
                Programs
              </TabsTrigger>
              <TabsTrigger 
                value="project-tracker"
                className="data-[state=active]:bg-[#00AEEF]/10 data-[state=active]:text-[#00AEEF] data-[state=active]:shadow-none rounded-lg px-4 text-[13px] font-medium text-[#4b5563]"
                data-testid="tab-project-tracker"
              >
                Project
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Scenario 3 Warning Banner - Critical TDC Alert */}
      {activeTab === "overview" && briSummary?.scenarioStatus?.level === 3 && (
        <div className="bg-red-50 border-b-2 border-red-400 px-6 py-4">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <span className="text-sm font-bold text-red-800">
                  Scenario 3 Triggered: Builder percentage at {briSummary?.coreBuilders?.percentage || 0}% — below 5% threshold
                </span>
                <p className="text-xs text-red-700 mt-0.5">
                  TDC (Technology Development Center) recommendation activated. External partner engagement recommended.
                </p>
              </div>
              <Button 
                variant="link" 
                size="sm" 
                className="text-red-700 font-semibold p-0 h-auto ml-4"
                onClick={() => setActiveTab("talent-intelligence")}
              >
                View details →
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-red-600 hover:text-red-800 hover:bg-red-100"
              onClick={() => setDismissedAlerts([...dismissedAlerts, 'scenario3'])}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Alert Banner */}
      {activeTab === "overview" && visibleAlerts.map((alert, index) => (
        <div 
          key={index}
          className="bg-amber-50 border-b border-amber-200 px-6 py-3"
        >
          <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">
                {alert.message}
              </span>
              <Button variant="link" size="sm" className="text-amber-700 p-0 h-auto">
                {alert.action}
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 text-amber-600 hover:text-amber-800"
              onClick={() => dismissAlert(index)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}

      {activeTab === "overview" && (
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Row 1: Primary KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          {/* Professionals */}
          <Card className="bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="kpi-professionals">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Professionals
              </p>
              <p className="text-3xl font-bold text-[#3b3b3b]">
                {stats?.total || 0}
              </p>
              <Progress value={stats?.completionRate || 0} className="h-1.5 mt-2" />
              <p className="text-xs text-muted-foreground mt-1">
                <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-500" />
                {stats?.completed || 0} Complete
              </p>
            </CardContent>
          </Card>

          {/* Completed */}
          <Card className="bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-completed">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Completed
              </p>
              <p className="text-3xl font-bold text-[#3b3b3b]">
                {stats?.completed || 0}
              </p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-xs text-muted-foreground">
                  All time completions
                </span>
              </div>
            </CardContent>
          </Card>

          {/* In Progress */}
          <Card className="bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-in-progress">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                In Progress
              </p>
              <div className="flex items-center justify-between">
                <p className="text-3xl font-bold text-blue-600">
                  {stats?.started || 0}
                </p>
                {weeklyTrend && <Sparkline data={weeklyTrend.trends.inProgress} color="#3b82f6" />}
              </div>
              <div className="flex items-center gap-1 mt-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-xs text-emerald-600">Active</span>
              </div>
            </CardContent>
          </Card>

          {/* Not Started */}
          <Card className="bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-not-started">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Not Started
              </p>
              <p className={`text-3xl font-bold ${
                notStartedPercentage > 50 ? 'text-red-600' : 
                notStartedPercentage > 25 ? 'text-amber-600' : 'text-slate-600'
              }`}>
                {stats?.pending || 0}
              </p>
              <p className={`text-xs mt-2 ${
                notStartedPercentage > 50 ? 'text-red-600' : 
                notStartedPercentage > 25 ? 'text-amber-600' : 'text-muted-foreground'
              }`}>
                {notStartedPercentage}% of total
              </p>
            </CardContent>
          </Card>

          {/* Completion Rate */}
          <Card className="bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="kpi-completion-rate">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Completion Rate
              </p>
              <p className={`text-3xl font-bold ${
                (stats?.completionRate || 0) >= 75 ? 'text-emerald-600' :
                (stats?.completionRate || 0) >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {stats?.completionRate || 0}%
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                <Target className="w-3 h-3 inline mr-1" />
                Completion Rate
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Manager Validation KPI Card */}
        {(() => {
          const mvStats = stats?.typeStats?.find(t => t.code === 'manager_validation');
          if (!mvStats || !mvStats.total) return null;
          const mvRate = mvStats.total > 0 ? Math.round((mvStats.completed / mvStats.total) * 100) : 0;
          return (
            <div className="mb-4">
              <Card className="bg-white shadow-sm hover:shadow-md transition-shadow border-l-4 border-l-[#8B5CF6]" data-testid="kpi-manager-validation">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Manager Validation
                      </p>
                      <p className="text-3xl font-bold text-[#8B5CF6]">{mvStats.total}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {mvStats.completed} completed · {mvRate}% rate · {mvStats.total - mvStats.completed} pending
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={mvRate} className="w-24 h-2" />
                      <Users className="w-6 h-6 text-[#8B5CF6]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })()}

        {/* Row 2: Builder Readiness Summary (BRI) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {/* Average BRI Score */}
          <Card className={`shadow-sm ${briSummary?.hasData ? 'bg-white' : 'bg-slate-50'}`} data-testid="kpi-bri-avg">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Average BRI Score
              </p>
              {briSummary?.hasData ? (
                <>
                  <p className={`text-3xl font-bold ${
                    (briSummary?.averageBRI || 0) >= 58 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {briSummary?.averageBRI || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Benchmark: {briSummary?.industryBenchmark}
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Awaiting Data</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Core Builders */}
          <Card className={`shadow-sm ${briSummary?.hasData ? 'bg-white' : 'bg-slate-50'}`} data-testid="kpi-core-builders">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Core Builders
              </p>
              {briSummary?.hasData ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-[#1B5E20]">
                      {briSummary?.coreBuilders?.count || 0}
                    </p>
                    <span className="text-sm text-muted-foreground">
                      ({briSummary?.coreBuilders?.percentage || 0}%)
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Target: &gt;{briSummary?.coreBuilders?.target}%
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Awaiting Data</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Emerging Builders */}
          <Card className={`shadow-sm ${briSummary?.hasData ? 'bg-white' : 'bg-slate-50'}`} data-testid="kpi-emerging-builders">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Emerging Builders
              </p>
              {briSummary?.hasData ? (
                <>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-[#1565C0]">
                      {briSummary?.emergingBuilders?.count || 0}
                    </p>
                    <span className="text-sm text-muted-foreground">
                      ({briSummary?.emergingBuilders?.percentage || 0}%)
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Development Pipeline
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Awaiting Data</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Scenario Status */}
          <Card className={`shadow-sm ${briSummary?.hasData ? 'bg-white' : 'bg-slate-50'}`} data-testid="kpi-scenario">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Scenario Status
              </p>
              {briSummary?.hasData && briSummary?.scenarioStatus ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${
                      briSummary.scenarioStatus.color === 'green' ? 'bg-emerald-500' :
                      briSummary.scenarioStatus.color === 'yellow' ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                    <p className={`text-lg font-bold ${
                      briSummary.scenarioStatus.color === 'green' ? 'text-emerald-700' :
                      briSummary.scenarioStatus.color === 'yellow' ? 'text-amber-700' : 'text-red-700'
                    }`}>
                      {briSummary.scenarioStatus.name}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                    {briSummary.scenarioStatus.description}
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Awaiting Data</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Development Investment */}
          <Card className={`shadow-sm ${briSummary?.hasData ? 'bg-white' : 'bg-slate-50'}`} data-testid="kpi-dev-investment">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
                Dev Investment
              </p>
              {briSummary?.hasData ? (
                <>
                  <p className="text-2xl font-bold text-[#3b3b3b]">
                    {formatCurrency(briSummary?.totalDevelopmentInvestment)}
                  </p>
                  <p className="text-xs text-emerald-600 mt-2">
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                    ROI: {formatCurrency(briSummary?.projectedROI)}
                  </p>
                </>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Awaiting Data</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Select a Country Section */}
        {regionFilter === "all" && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-[#3b3b3b] mb-4">Select a Country</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats?.regionStats?.map((region) => {
                // Find segment data for this region
                const regionSegments = segmentDistribution?.distribution?.find(d => d.region === region.code);
                const hasSegmentData = regionSegments && regionSegments.total > 0;
                
                return (
                  <button
                    key={region.code}
                    onClick={() => { setRegionFilter(region.code); setCurrentPage(1); }}
                    className="group relative p-5 bg-white rounded-xl shadow-sm hover:shadow-md transition-all border border-slate-200 hover:border-[#1a3a3a]/30 text-left"
                    data-testid={`region-card-${region.code}`}
                  >
                    {/* Health indicator */}
                    <div className={`absolute top-3 right-3 w-2.5 h-2.5 rounded-full ${
                      region.completionRate >= 60 ? 'bg-emerald-500' :
                      region.completionRate >= 30 ? 'bg-amber-500' : 'bg-red-500'
                    }`} />
                    
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-3xl">{region.flag}</span>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#3b3b3b] transition-colors" />
                    </div>
                    <h3 className="font-semibold text-[#3b3b3b] mb-1">{region.name}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <span>{region.total} employees</span>
                      <span>·</span>
                      <span className={region.completionRate >= 50 ? 'text-emerald-600' : 'text-amber-600'}>
                        {region.completionRate}% complete
                      </span>
                    </div>
                    
                    {/* Mini segment bar */}
                    {hasSegmentData && (
                      <div className="flex h-2 rounded-full overflow-hidden mt-3">
                        <div style={{ width: `${regionSegments.coreBuilderPct}%`, backgroundColor: '#1B5E20' }} />
                        <div style={{ width: `${regionSegments.emergingBuilderPct}%`, backgroundColor: '#1565C0' }} />
                        <div style={{ width: `${regionSegments.supportDeveloperPct}%`, backgroundColor: '#F9A825' }} />
                        <div style={{ width: `${regionSegments.transitionCandidatePct}%`, backgroundColor: '#C62828' }} />
                      </div>
                    )}
                    
                    <Progress value={region.completionRate} className="mt-2 h-1.5" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Region Filter Active Banner */}
        {regionFilter !== "all" && (
          <div className="mb-6 flex items-center justify-between p-4 bg-[#1a3a3a]/5 rounded-lg border border-[#1a3a3a]/10">
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {stats?.regionStats?.find(r => r.code === regionFilter)?.flag}
              </span>
              <div>
                <p className="font-semibold text-[#3b3b3b]">
                  Viewing: {stats?.regionStats?.find(r => r.code === regionFilter)?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {stats?.regionStats?.find(r => r.code === regionFilter)?.total} employees · {stats?.regionStats?.find(r => r.code === regionFilter)?.completionRate}% completion rate
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setRegionFilter("all"); setCurrentPage(1); }}
              className="gap-2"
              data-testid="clear-region-filter"
            >
              <X className="w-4 h-4" />
              Clear Filter
            </Button>
          </div>
        )}

        {/* Row 4: THREE CHARTS SIDE BY SIDE */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Chart 1 — Completion by Region (Donut) */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Completion by Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="relative w-32 h-32">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {stats?.regionStats && (() => {
                      let offset = 0;
                      const total = stats.regionStats.reduce((sum, r) => sum + r.total, 0) || 1;
                      const colors = { usa: '#1B5E20', canada: '#1565C0', jamaica: '#F59E0B', barbados: '#7C3AED', trinidad_tobago: '#E91E63', panama: '#FF5722' };
                      return stats.regionStats.map((region) => {
                        const percentage = (region.total / total) * 100;
                        const dashArray = `${percentage} ${100 - percentage}`;
                        const circle = (
                          <circle
                            key={region.code}
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={colors[region.code] || '#94a3b8'}
                            strokeWidth="20"
                            strokeDasharray={dashArray}
                            strokeDashoffset={-offset}
                          />
                        );
                        offset += percentage;
                        return circle;
                      });
                    })()}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-2xl font-bold text-[#3b3b3b]">{stats?.total || 0}</p>
                    <p className="text-[10px] text-muted-foreground">Total</p>
                  </div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {stats?.regionStats?.map((region) => (
                    <div key={region.code} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: { usa: '#1B5E20', canada: '#1565C0', jamaica: '#F59E0B', barbados: '#7C3AED', trinidad_tobago: '#E91E63', panama: '#FF5722' }[region.code] || '#94a3b8' }} />
                        <span>{region.flag} {region.name.split(' ')[0]}</span>
                      </div>
                      <span className="font-medium">{region.total} ({region.completionRate}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart 2 — Segment Distribution Stacked Bars */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Segment Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {segmentDistribution?.distribution?.filter(d => d.region !== 'all').slice(0, 4).map((item) => (
                  <div key={item.region} className="space-y-0.5">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="font-medium">{item.flag} {item.regionName?.split(' ')[0]}</span>
                      <span className="text-muted-foreground">{item.total}</span>
                    </div>
                    <div className="h-4 rounded overflow-hidden flex bg-slate-100">
                      {item.total > 0 ? (
                        <>
                          <div style={{ width: `${item.coreBuilderPct}%`, backgroundColor: '#1B5E20' }} className="flex items-center justify-center">
                            {item.coreBuilderPct > 10 && <span className="text-[8px] text-white">{item.coreBuilder}</span>}
                          </div>
                          <div style={{ width: `${item.emergingBuilderPct}%`, backgroundColor: '#1565C0' }} className="flex items-center justify-center">
                            {item.emergingBuilderPct > 10 && <span className="text-[8px] text-white">{item.emergingBuilder}</span>}
                          </div>
                          <div style={{ width: `${item.supportDeveloperPct}%`, backgroundColor: '#F9A825' }} className="flex items-center justify-center">
                            {item.supportDeveloperPct > 10 && <span className="text-[8px]">{item.supportDeveloper}</span>}
                          </div>
                          <div style={{ width: `${item.transitionCandidatePct}%`, backgroundColor: '#C62828' }} className="flex items-center justify-center">
                            {item.transitionCandidatePct > 10 && <span className="text-[8px] text-white">{item.transitionCandidate}</span>}
                          </div>
                        </>
                      ) : (
                        <span className="text-[8px] text-slate-400 px-2 flex items-center">No data</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t">
                <span className="flex items-center gap-1 text-[9px]"><div className="w-2 h-2 rounded" style={{ backgroundColor: '#1B5E20' }} />Core</span>
                <span className="flex items-center gap-1 text-[9px]"><div className="w-2 h-2 rounded" style={{ backgroundColor: '#1565C0' }} />Emerging</span>
                <span className="flex items-center gap-1 text-[9px]"><div className="w-2 h-2 rounded" style={{ backgroundColor: '#F9A825' }} />Support</span>
                <span className="flex items-center gap-1 text-[9px]"><div className="w-2 h-2 rounded" style={{ backgroundColor: '#C62828' }} />Transition</span>
              </div>
            </CardContent>
          </Card>

          {/* Chart 3 — BRI Score Distribution (Histogram) */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                BRI Score Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {briSummary?.hasData ? (
                <div>
                  <div className="flex items-end gap-1 h-20 mb-2">
                    {[
                      { range: '0-54', count: briSummary?.transitionCandidates?.count || 0, color: '#C62828' },
                      { range: '55-69', count: briSummary?.supportDevelopers?.count || 0, color: '#F9A825' },
                      { range: '70-84', count: briSummary?.emergingBuilders?.count || 0, color: '#1565C0' },
                      { range: '85+', count: briSummary?.coreBuilders?.count || 0, color: '#1B5E20' }
                    ].map((bar, i) => {
                      const maxCount = Math.max(briSummary?.transitionCandidates?.count || 1, briSummary?.supportDevelopers?.count || 1, briSummary?.emergingBuilders?.count || 1, briSummary?.coreBuilders?.count || 1);
                      const height = maxCount > 0 ? (bar.count / maxCount) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <span className="text-[9px] font-medium mb-0.5">{bar.count}</span>
                          <div 
                            className="w-full rounded-t" 
                            style={{ height: `${Math.max(height, bar.count > 0 ? 15 : 0)}%`, backgroundColor: bar.color }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-1 text-[8px] text-muted-foreground border-t pt-1">
                    <span className="flex-1 text-center">&lt;55</span>
                    <span className="flex-1 text-center">55-69</span>
                    <span className="flex-1 text-center">70-84</span>
                    <span className="flex-1 text-center">85+</span>
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground mt-2 pt-2 border-t">
                    <span>Mean: <strong className="text-[#3b3b3b]">{briSummary?.averageBRI}</strong></span>
                    <span>Benchmark: <strong>58</strong></span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Lock className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                  <p className="text-xs">Awaiting data</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Row 5: THREE PANELS — Performance, Actions, Financial */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Panel 1 — Performance by Region Table */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Performance by Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 font-medium text-muted-foreground">Region</th>
                      <th className="text-center py-1.5 font-medium text-muted-foreground">Staff</th>
                      <th className="text-center py-1.5 font-medium text-muted-foreground">Avg BRI</th>
                      <th className="text-center py-1.5 font-medium text-muted-foreground">Builders</th>
                      <th className="text-center py-1.5 font-medium text-muted-foreground">Health</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.regionStats?.map((region) => {
                      const regionSegments = segmentDistribution?.distribution?.find(d => d.region === region.code);
                      const builderCount = regionSegments?.coreBuilder || 0;
                      const builderPct = regionSegments?.total > 0 ? Math.round((builderCount / regionSegments.total) * 100) : 0;
                      const avgBRI = regionSegments?.avgBRI || 0; // Use actual BRI from data
                      return (
                        <tr key={region.code} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="py-1.5">{region.flag} {region.name.split('(')[0].trim()}</td>
                          <td className="py-1.5 text-center">{region.total}</td>
                          <td className={`py-1.5 text-center font-medium ${avgBRI >= 58 ? 'text-emerald-600' : avgBRI > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {avgBRI || '—'}
                          </td>
                          <td className={`py-1.5 text-center font-medium ${builderPct >= 15 ? 'text-emerald-600' : builderPct >= 5 ? 'text-amber-600' : builderPct > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                            {builderCount} ({builderPct}%)
                          </td>
                          <td className="py-1.5 text-center">
                            <div className={`w-2.5 h-2.5 rounded-full mx-auto ${
                              region.completionRate >= 60 ? 'bg-emerald-500' :
                              region.completionRate >= 30 ? 'bg-amber-500' : 'bg-red-500'
                            }`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Panel 2 — Recommended Actions Summary */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Recommended Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { action: 'RETAIN', count: employees.filter(e => e.recommendedAction === 'Retain').length, color: '#1B5E20', desc: 'Replacement risk avoided', value: employees.filter(e => e.recommendedAction === 'Retain').length > 0 ? '$810K' : '$0' },
                { action: 'DEVELOP', count: employees.filter(e => e.recommendedAction === 'Develop').length, color: '#1565C0', desc: 'Investment → projected value', value: employees.filter(e => e.recommendedAction === 'Develop').length > 0 ? '$1.8M' : '$0' },
                { action: 'REASSIGN', count: employees.filter(e => e.recommendedAction === 'Reassign').length, color: '#E65100', desc: 'Productivity optimization', value: employees.filter(e => e.recommendedAction === 'Reassign').length > 0 ? '15%' : '0%' },
                { action: 'TRANSITION', count: employees.filter(e => e.recommendedAction === 'Transition').length, color: '#C62828', desc: 'Annual savings', value: employees.filter(e => e.recommendedAction === 'Transition').length > 0 ? '$6.8M' : '$0' }
              ].map((item) => {
                const pct = stats?.total > 0 ? Math.round((item.count / stats.total) * 100) : 0;
                return (
                  <div key={item.action} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: item.color }}>
                          {item.action}
                        </span>
                        <span className="text-xs font-medium">{item.count}</span>
                        <span className="text-[10px] text-muted-foreground">({pct}%)</span>
                      </div>
                      <span className="text-[10px] font-medium" style={{ color: item.color }}>{item.value}</span>
                    </div>
                    <Progress value={pct} className="h-1.5" style={{ '--progress-background': item.color }} />
                    <p className="text-[9px] text-muted-foreground">{item.desc}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Panel 3 — Financial Snapshot */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Financial Snapshot
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">One-Time Costs</p>
                <p className="text-xl font-bold text-slate-400">$0</p>
                <p className="text-[9px] text-muted-foreground">Awaiting data</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">Annual Savings</p>
                <p className="text-xl font-bold text-slate-400">$0</p>
                <p className="text-[9px] text-muted-foreground">Awaiting data</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium">3-Year NPV</p>
                <p className="text-xl font-bold text-slate-400">$0</p>
                <p className="text-[9px] text-muted-foreground">Awaiting data</p>
              </div>
              <div className="text-center py-2 bg-slate-50 rounded-lg border">
                <p className="text-xs font-medium text-slate-400">Financial projections will appear once assessments are completed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Segment Distribution Chart */}
            {segmentDistribution?.distribution && segmentDistribution.distribution.some(d => d.total > 0) && (
              <Card className="bg-white shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                    Talent Segment Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {segmentDistribution.distribution.map((item) => (
                      <div key={item.region} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium flex items-center gap-2">
                            <span>{item.flag}</span>
                            <span>{item.regionName}</span>
                          </span>
                          <span className="text-muted-foreground">{item.total} scored</span>
                        </div>
                        <div className="relative h-6 rounded-md overflow-hidden bg-slate-100 flex">
                          {item.total > 0 ? (
                            <>
                              <div 
                                className="h-full flex items-center justify-center text-[10px] font-medium text-white"
                                style={{ width: `${item.coreBuilderPct}%`, backgroundColor: '#1B5E20', minWidth: item.coreBuilderPct > 0 ? '20px' : '0' }}
                              >
                                {item.coreBuilderPct > 8 && `${item.coreBuilderPct}%`}
                              </div>
                              <div 
                                className="h-full flex items-center justify-center text-[10px] font-medium text-white"
                                style={{ width: `${item.emergingBuilderPct}%`, backgroundColor: '#1565C0', minWidth: item.emergingBuilderPct > 0 ? '20px' : '0' }}
                              >
                                {item.emergingBuilderPct > 8 && `${item.emergingBuilderPct}%`}
                              </div>
                              <div 
                                className="h-full flex items-center justify-center text-[10px] font-medium"
                                style={{ width: `${item.supportDeveloperPct}%`, backgroundColor: '#F9A825', minWidth: item.supportDeveloperPct > 0 ? '20px' : '0' }}
                              >
                                {item.supportDeveloperPct > 8 && `${item.supportDeveloperPct}%`}
                              </div>
                              <div 
                                className="h-full flex items-center justify-center text-[10px] font-medium text-white"
                                style={{ width: `${item.transitionCandidatePct}%`, backgroundColor: '#C62828', minWidth: item.transitionCandidatePct > 0 ? '20px' : '0' }}
                              >
                                {item.transitionCandidatePct > 8 && `${item.transitionCandidatePct}%`}
                              </div>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 px-2 flex items-center">No data</span>
                          )}
                          {/* Threshold lines */}
                          <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-emerald-600/50" style={{ left: '15%' }} title="15% Builder threshold" />
                          <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-600/50" style={{ left: '5%' }} title="5% Critical threshold" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#1B5E20' }} />
                      <span className="text-xs">Core Builder</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#1565C0' }} />
                      <span className="text-xs">Emerging</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F9A825' }} />
                      <span className="text-xs">Support</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: '#C62828' }} />
                      <span className="text-xs">Transition</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enhanced All Employees Table */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                      All Employees
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">{employeesTotal}</Badge>
                  </div>
                  <Select value={segmentFilter} onValueChange={(v) => { setSegmentFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[160px] h-8 text-xs">
                      <SelectValue placeholder="Filter by segment" />
                    </SelectTrigger>
                    <SelectContent>
                      {SEGMENTS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Status Tabs */}
                <div className="flex gap-1 mt-3">
                  {['all', 'completed', 'started', 'pending'].map((status) => (
                    <Button
                      key={status}
                      variant={statusFilter === status ? "default" : "ghost"}
                      size="sm"
                      onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                      className={`text-xs h-7 ${statusFilter === status ? 'bg-[#1a3a3a]' : ''}`}
                    >
                      {status === 'all' ? 'All' : 
                       status === 'completed' ? 'Completed' :
                       status === 'started' ? 'In Progress' : 'Not Started'}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("fullName")}>
                          <div className="flex items-center gap-1">
                            Name
                            {sortBy === "fullName" && (sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                          </div>
                        </th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden lg:table-cell">Email</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden md:table-cell">Manager</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("region")}>
                          <div className="flex items-center gap-1">
                            Region
                            {sortBy === "region" && (sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                          </div>
                        </th>
                        <th className="text-center py-2 px-2 font-medium text-muted-foreground">BRI</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden xl:table-cell">Segment</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground hidden xl:table-cell">Action</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground">Phase</th>
                        <th className="text-left py-2 px-2 font-medium text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort("status")}>
                          <div className="flex items-center gap-1">
                            Status
                            {sortBy === "status" && (sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr 
                          key={emp.id}
                          className="border-b last:border-0 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/executive/profile/${emp.id}?from=overview`)}
                          data-testid={`employee-row-${emp.id}`}
                        >
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-[#1a3a3a]/10 rounded-full flex items-center justify-center text-xs font-medium text-[#3b3b3b]">
                                {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{emp.fullName}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[120px]">{emp.jobTitle || 'Employee'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2 hidden lg:table-cell">
                            <span className="text-xs text-muted-foreground truncate block max-w-[150px]" title={emp.email}>
                              {emp.email}
                            </span>
                          </td>
                          <td className="py-3 px-2 hidden md:table-cell">
                            <span className="text-xs truncate block max-w-[100px]" title={emp.managerName}>
                              {emp.managerName || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-lg" title={emp.region}>{emp.regionFlag}</span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            {emp.briScore ? (
                              <span className={`font-bold ${
                                emp.briScore >= 85 ? 'text-[#1B5E20]' :
                                emp.briScore >= 70 ? 'text-[#1565C0]' :
                                emp.briScore >= 55 ? 'text-[#F9A825]' : 'text-[#C62828]'
                              }`}>
                                {emp.briScore}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="py-3 px-2 hidden xl:table-cell">
                            <SegmentBadge segment={emp.segment} />
                          </td>
                          <td className="py-3 px-2 hidden xl:table-cell">
                            <ActionBadge action={emp.recommendedAction} />
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {emp.currentPhase || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            {emp.status === 'completed' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                Completed
                              </Badge>
                            ) : emp.status === 'started' ? (
                              <div className="flex items-center gap-2">
                                <Progress value={emp.progressPercentage} className="w-12 h-2" />
                                <span className="text-xs text-muted-foreground">{emp.progressPercentage}%</span>
                              </div>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">
                                Not Started
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, employeesTotal)} of {employeesTotal}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="h-8"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="h-8"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - 1/3 */}
          <div className="space-y-6">
            {/* Live Activity Feed */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                      Live Activity
                    </CardTitle>
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs h-7">View All</Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[280px]">
                  <div className="space-y-3">
                    {activity.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No recent activity
                      </p>
                    ) : (
                      activity.map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            item.eventType === 'completed' ? 'bg-emerald-100' : 'bg-amber-100'
                          }`}>
                            {item.eventType === 'completed' ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Activity className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">
                              <span className="font-medium">{item.candidateName}</span>
                              <span className="text-muted-foreground">
                                {item.eventType === 'completed' ? ' completed' : ' started'}
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <span>{item.regionFlag}</span>
                              <span>·</span>
                              <span>Self Assessment</span>
                              <span>·</span>
                              <span>{formatTimeAgo(item.timestamp)}</span>
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Performance by Region */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                  Performance by Region
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-3 pb-2 border-b">
                  <div>Region</div>
                  <div className="text-center">Total</div>
                  <div className="text-center">Done</div>
                  <div className="text-center">Active</div>
                  <div className="text-right">Rate</div>
                </div>
                <div className="space-y-2">
                  {stats?.regionStats?.map((region) => (
                    <button
                      key={region.code}
                      onClick={() => { setRegionFilter(region.code); setCurrentPage(1); }}
                      className={`w-full grid grid-cols-5 gap-2 items-center p-2 rounded-lg hover:bg-slate-50 transition-colors ${regionFilter === region.code ? 'bg-slate-100' : ''}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{region.flag}</span>
                        <span className="text-xs font-medium truncate">{region.name.split(' ')[0]}</span>
                      </div>
                      <div className="text-center text-xs">{region.total}</div>
                      <div className="text-center text-xs text-emerald-600">{region.completed}</div>
                      <div className="text-center text-xs text-amber-600">{region.total - region.completed}</div>
                      <div className="text-right">
                        <span className={`text-xs font-semibold ${
                          region.completionRate >= 50 ? 'text-emerald-600' : 
                          region.completionRate >= 25 ? 'text-amber-600' : 'text-red-500'
                        }`}>
                          {region.completionRate}%
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Assessment Types */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                  Assessment Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.typeStats?.map((type) => (
                    <div key={type.code} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{type.name}</span>
                        <Badge 
                          variant={type.active ? "default" : "outline"}
                          className={`text-[10px] ${type.active ? 'bg-emerald-100 text-emerald-700 border-0' : ''}`}
                        >
                          {type.active ? 'ACTIVE' : 'UPCOMING'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={type.total > 0 ? (type.completed / type.total) * 100 : 0} 
                          className="flex-1 h-2"
                        />
                        <span className="text-xs text-muted-foreground w-16 text-right">
                          {type.completed}/{type.total}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                  Assessment Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { month: 'Dec 2025', label: 'Setup Complete', done: true },
                    { month: 'Jan 2026', label: 'Self Assessments', active: true },
                    { month: 'Feb 2026', label: 'Technical', done: false },
                    { month: 'Mar 2026', label: 'Manager Validation', done: false },
                    { month: 'Apr 2026', label: 'Final Reports', done: false }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        item.done ? 'bg-emerald-500' : 
                        item.active ? 'bg-blue-500 animate-pulse' : 'bg-slate-300'
                      }`} />
                      <span className={`text-xs flex-1 ${item.active ? 'font-medium' : 'text-muted-foreground'}`}>
                        {item.month} — {item.label}
                      </span>
                      {item.active && <Badge className="text-[10px] h-4 bg-blue-100 text-blue-700 border-0">ACTIVE</Badge>}
                      {item.done && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {/* Completion Breakdown */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Completion Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm flex-1">Completed</span>
                <Progress value={(stats?.completed / stats?.total) * 100 || 0} className="w-24 h-2" />
                <span className="text-sm font-medium w-10 text-right">{stats?.completed || 0}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm flex-1">In Progress</span>
                <Progress value={(stats?.started / stats?.total) * 100 || 0} className="w-24 h-2" />
                <span className="text-sm font-medium w-10 text-right">{stats?.started || 0}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-slate-300" />
                <span className="text-sm flex-1">Not Started</span>
                <Progress value={(stats?.pending / stats?.total) * 100 || 0} className="w-24 h-2" />
                <span className="text-sm font-medium w-10 text-right">{stats?.pending || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Today's Activity */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Today&apos;s Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Completed today</span>
                  <span className="font-semibold">{stats?.completedToday || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Started today</span>
                  <span className="font-semibold">{activity.filter(a => a.eventType === 'started').length}</span>
                </div>
                <div className="pt-2 border-t">
                  <p className={`text-xs flex items-center gap-1 ${getTrendColor(stats?.completedToday || 0, stats?.completedYesterday || 0)}`}>
                    {getTrendIcon(stats?.completedToday || 0, stats?.completedYesterday || 0)}
                    {(stats?.completedToday || 0) - (stats?.completedYesterday || 0) >= 0 ? '+' : ''}
                    {(stats?.completedToday || 0) - (stats?.completedYesterday || 0)} vs yesterday
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Donut Chart Summary */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#3b3b3b]">
                Completion by Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    {stats?.regionStats && (() => {
                      let offset = 0;
                      const total = stats.regionStats.reduce((sum, r) => sum + r.total, 0) || 1;
                      return stats.regionStats.map((region, i) => {
                        const percentage = (region.total / total) * 100;
                        const dashArray = `${percentage} ${100 - percentage}`;
                        const circle = (
                          <circle
                            key={region.code}
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke={REGION_COLORS[region.code] || '#94a3b8'}
                            strokeWidth="20"
                            strokeDasharray={dashArray}
                            strokeDashoffset={-offset}
                          />
                        );
                        offset += percentage;
                        return circle;
                      });
                    })()}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <p className="text-lg font-bold text-[#3b3b3b]">{stats?.total || 0}</p>
                  </div>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-1">
                  {stats?.regionStats?.map((region) => (
                    <div key={region.code} className="flex items-center gap-1">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: REGION_COLORS[region.code] }}
                      />
                      <span className="text-xs truncate">{region.flag} {region.total}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      )}

      {/* Talent Intelligence Tab */}
      {/* Tab: Technical Assessment */}
      {activeTab === "technical-assessment" && (
        <TechnicalAssessmentTab />
      )}

      {/* Tab 2: Talent Intelligence */}
      {activeTab === "talent-intelligence" && (
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* BRI Distribution Histogram */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                  Builder Readiness Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {briSummary?.hasData ? (
                  <div>
                    {/* Simplified distribution bars */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-24 text-right">Core (85-100)</span>
                        <div className="flex-1 h-8 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="h-full bg-[#1B5E20] flex items-center justify-end pr-2"
                            style={{ width: `${briSummary.coreBuilders?.percentage || 0}%`, minWidth: briSummary.coreBuilders?.count > 0 ? '40px' : '0' }}
                          >
                            <span className="text-white text-xs font-medium">{briSummary.coreBuilders?.count}</span>
                          </div>
                        </div>
                        <span className="text-xs w-12">{briSummary.coreBuilders?.percentage}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-24 text-right">Emerging (70-84)</span>
                        <div className="flex-1 h-8 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="h-full bg-[#1565C0] flex items-center justify-end pr-2"
                            style={{ width: `${briSummary.emergingBuilders?.percentage || 0}%`, minWidth: briSummary.emergingBuilders?.count > 0 ? '40px' : '0' }}
                          >
                            <span className="text-white text-xs font-medium">{briSummary.emergingBuilders?.count}</span>
                          </div>
                        </div>
                        <span className="text-xs w-12">{briSummary.emergingBuilders?.percentage}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-24 text-right">Support (55-69)</span>
                        <div className="flex-1 h-8 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="h-full bg-[#F9A825] flex items-center justify-end pr-2"
                            style={{ width: `${briSummary.supportDevelopers?.percentage || 0}%`, minWidth: briSummary.supportDevelopers?.count > 0 ? '40px' : '0' }}
                          >
                            <span className="text-xs font-medium">{briSummary.supportDevelopers?.count}</span>
                          </div>
                        </div>
                        <span className="text-xs w-12">{briSummary.supportDevelopers?.percentage}%</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs w-24 text-right">Transition (&lt;55)</span>
                        <div className="flex-1 h-8 bg-slate-100 rounded relative overflow-hidden">
                          <div 
                            className="h-full bg-[#C62828] flex items-center justify-end pr-2"
                            style={{ width: `${briSummary.transitionCandidates?.percentage || 0}%`, minWidth: briSummary.transitionCandidates?.count > 0 ? '40px' : '0' }}
                          >
                            <span className="text-white text-xs font-medium">{briSummary.transitionCandidates?.count}</span>
                          </div>
                        </div>
                        <span className="text-xs w-12">{briSummary.transitionCandidates?.percentage}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t text-sm">
                      <div>
                        <span className="text-muted-foreground">Mean BRI: </span>
                        <span className="font-bold text-[#3b3b3b]">{briSummary.averageBRI}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Benchmark: </span>
                        <span className="font-medium">{briSummary.industryBenchmark}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Scored: </span>
                        <span className="font-medium">{briSummary.totalScored}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p>Distribution will appear once assessments are scored</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Scenario Assessment Panel */}
            <Card className="bg-white shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                  Scenario Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-3">
                  {/* Scenario 1 */}
                  <div className={`p-4 rounded-lg border-2 ${
                    briSummary?.scenarioStatus?.level === 1 ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${
                        briSummary?.scenarioStatus?.level === 1 ? 'bg-emerald-500' : 'bg-slate-300'
                      }`} />
                      <h4 className="font-semibold text-sm">Internal Build</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">&gt;15% builders</p>
                    <Badge className={briSummary?.scenarioStatus?.level === 1 ? 'bg-emerald-600' : 'bg-slate-400'}>
                      {briSummary?.scenarioStatus?.level === 1 ? 'ACTIVE' : 'NOT MET'}
                    </Badge>
                    {briSummary?.scenarioStatus?.level === 1 && (
                      <p className="text-xs mt-2 text-emerald-700">Internal team can transform organically</p>
                    )}
                  </div>

                  {/* Scenario 2 */}
                  <div className={`p-4 rounded-lg border-2 ${
                    briSummary?.scenarioStatus?.level === 2 ? 'border-amber-500 bg-amber-50' : 'border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${
                        briSummary?.scenarioStatus?.level === 2 ? 'bg-amber-500' : 'bg-slate-300'
                      }`} />
                      <h4 className="font-semibold text-sm">Hybrid Model</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">5-15% builders</p>
                    <Badge className={briSummary?.scenarioStatus?.level === 2 ? 'bg-amber-600' : 'bg-slate-400'}>
                      {briSummary?.scenarioStatus?.level === 2 ? 'ACTIVE' : 'NOT MET'}
                    </Badge>
                    {briSummary?.scenarioStatus?.level === 2 && (
                      <p className="text-xs mt-2 text-amber-700">Selective external augmentation recommended</p>
                    )}
                  </div>

                  {/* Scenario 3 */}
                  <div className={`p-4 rounded-lg border-2 ${
                    briSummary?.scenarioStatus?.level === 3 ? 'border-red-500 bg-red-50' : 'border-slate-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${
                        briSummary?.scenarioStatus?.level === 3 ? 'bg-red-500' : 'bg-slate-300'
                      }`} />
                      <h4 className="font-semibold text-sm">TDC Required</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">&lt;5% builders</p>
                    <Badge className={briSummary?.scenarioStatus?.level === 3 ? 'bg-red-600' : 'bg-slate-400'}>
                      {briSummary?.scenarioStatus?.level === 3 ? 'TRIGGERED' : 'NOT MET'}
                    </Badge>
                    {briSummary?.scenarioStatus?.level === 3 && (
                      <p className="text-xs mt-2 text-red-700">Technology Development Center with external partner recommended</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Financial Impact Summary */}
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                Financial Impact Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-red-50 rounded-xl text-center border border-red-200">
                  <p className="text-xs text-red-600 uppercase tracking-wide mb-1">One-Time Costs</p>
                  <p className="text-3xl font-bold text-red-700">{formatCurrency(briSummary?.totalDevelopmentInvestment || 0)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Development + Severance</p>
                </div>
                <div className="p-6 bg-emerald-50 rounded-xl text-center border border-emerald-200">
                  <p className="text-xs text-emerald-600 uppercase tracking-wide mb-1">Annual Savings</p>
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency((briSummary?.projectedROI || 0) * 0.4)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Productivity + Retention</p>
                </div>
                <div className="p-6 bg-blue-50 rounded-xl text-center border border-blue-200">
                  <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">3-Year NPV</p>
                  <p className="text-3xl font-bold text-blue-700">{formatCurrency(briSummary?.projectedROI || 0)}</p>
                  <p className="text-xs text-muted-foreground mt-1">Net Present Value</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skills Gap Heat Map */}
          <Card className="bg-white shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                Skills Gap Heat Map by Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Region</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Programming</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">DevOps</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Security</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Testing</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Data/ML</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Architecture</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">Automation</th>
                      <th className="text-center py-2 px-2 font-medium text-muted-foreground text-xs">AI-Aug</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { region: 'USA', flag: '🇺🇸', scores: [72, 65, 78, 70, 45, 68, 55, 42] },
                      { region: 'Canada', flag: '🇨🇦', scores: [68, 70, 75, 72, 50, 65, 58, 48] },
                      { region: 'Jamaica', flag: '🇯🇲', scores: [60, 55, 62, 58, 38, 52, 45, 35] },
                      { region: 'S. Caribbean', flag: '🇹🇹', scores: [0, 0, 0, 0, 0, 0, 0, 0], noData: true }
                    ].map((row) => (
                      <tr key={row.region} className="border-b last:border-0">
                        <td className="py-2 px-3 font-medium">
                          <span className="mr-2">{row.flag}</span>{row.region}
                          {row.noData && <span className="text-xs text-muted-foreground ml-2">(No data)</span>}
                        </td>
                        {row.scores.map((score, i) => (
                          <td key={i} className="py-2 px-2 text-center">
                            <div className={`w-10 h-8 mx-auto rounded flex items-center justify-center text-xs font-medium ${
                              row.noData ? 'bg-gray-100 text-gray-400' :
                              score >= 70 ? 'bg-emerald-600 text-white' :
                              score >= 55 ? 'bg-emerald-200 text-emerald-800' :
                              score >= 40 ? 'bg-amber-200 text-amber-800' : 'bg-red-200 text-red-800'
                            }`}>
                              {row.noData ? '-' : score}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-600 rounded" />
                  <span className="text-xs">Strong (70+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-emerald-200 rounded" />
                  <span className="text-xs">Adequate (55-69)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-200 rounded" />
                  <span className="text-xs">Developing (40-54)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-200 rounded" />
                  <span className="text-xs">Critical (&lt;40)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Core Builders & Flight Risk Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Core Builders (85+) */}
            <Card className="bg-white shadow-sm border-l-4 border-l-[#1B5E20]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                    Core Builders (85+)
                  </CardTitle>
                  <Badge className="bg-[#1B5E20] text-white">
                    {employees.filter(e => (e.briScore || 0) >= 85).length} employees
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {employees.filter(e => (e.briScore || 0) >= 85).length > 0 ? (
                  <div className="space-y-2">
                    {employees.filter(e => (e.briScore || 0) >= 85).sort((a, b) => (b.briScore || 0) - (a.briScore || 0)).slice(0, 10).map((emp, i) => (
                      <div 
                        key={emp.id} 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-emerald-50 cursor-pointer border border-transparent hover:border-emerald-200"
                        onClick={() => navigate(`/executive/profile/${emp.id}?from=talent-intelligence`)}
                      >
                        <span className="text-xs font-bold text-[#1B5E20] w-4">{i + 1}</span>
                        <div className="w-8 h-8 bg-[#1B5E20]/10 rounded-full flex items-center justify-center text-xs font-bold text-[#1B5E20]">
                          {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{emp.fullName}</p>
                          <p className="text-xs text-muted-foreground">{emp.regionFlag} {emp.jobTitle}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xl font-bold text-[#1B5E20]">{emp.briScore}</span>
                          <p className="text-[9px] text-muted-foreground">Replace: ${Math.round((emp.briScore || 70) * 1500).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Trophy className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm">No Core Builders yet (85+ BRI required)</p>
                    <p className="text-xs mt-1">Complete assessments to identify top talent</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Highest Flight Risk (30%+) */}
            <Card className="bg-white shadow-sm border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold text-[#3b3b3b]">
                    Highest Flight Risk (≥30%)
                  </CardTitle>
                  <Badge className="bg-red-600 text-white">
                    {employees.filter(e => (e.flightRisk || 0) >= 30).length} at risk
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {employees.filter(e => (e.flightRisk || 0) >= 30).length > 0 ? (
                  <div className="space-y-2">
                    {employees.filter(e => (e.flightRisk || 0) >= 30).sort((a, b) => (b.flightRisk || 0) - (a.flightRisk || 0)).slice(0, 10).map((emp, i) => (
                      <div 
                        key={emp.id} 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-red-50 cursor-pointer border border-transparent hover:border-red-200"
                        onClick={() => navigate(`/executive/profile/${emp.id}?from=talent-intelligence`)}
                      >
                        <span className="text-xs font-bold text-red-600 w-4">{i + 1}</span>
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-xs font-bold text-red-700">
                          {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{emp.fullName}</p>
                          <p className="text-xs text-muted-foreground">{emp.regionFlag} {emp.jobTitle}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <SegmentBadge segment={emp.segment} />
                          <Badge className={`${
                            (emp.flightRisk || 0) >= 50 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
                          }`}>
                            {emp.flightRisk}%
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
                    <p className="text-sm text-emerald-600 font-medium">No high-risk employees</p>
                    <p className="text-xs mt-1">All employees below 30% flight risk threshold</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      )}

      {activeTab === "communications" && (
        <CommunicationHub tokenKey="executiveToken" userRole="executive" />
      )}

      {/* Tab 3: Profiles - Employee Directory */}
      {activeTab === "profiles" && (
        <main className="max-w-[1600px] mx-auto px-6 py-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#3b3b3b]">Employee Profiles</h2>
            <p className="text-sm text-muted-foreground">Click on any employee to view their detailed profile</p>
          </div>
          
          {/* Employee Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {employees.slice(0, 20).map((emp) => (
              <Card 
                key={emp.id}
                className="bg-white shadow-sm hover:shadow-md transition-all cursor-pointer border-l-4"
                style={{ borderLeftColor: emp.briScore >= 85 ? '#1B5E20' : emp.briScore >= 70 ? '#1565C0' : emp.briScore >= 55 ? '#E65100' : '#B71C1C' }}
                onClick={() => navigate(`/executive/profile/${emp.id}?from=profiles`)}
                data-testid={`profile-card-${emp.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                      emp.briScore >= 85 ? 'bg-[#1B5E20]' : emp.briScore >= 70 ? 'bg-[#1565C0]' : emp.briScore >= 55 ? 'bg-[#E65100]' : 'bg-[#B71C1C]'
                    }`}>
                      {emp.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{emp.fullName}</p>
                      <p className="text-xs text-muted-foreground truncate">{emp.jobTitle}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs">{emp.regionFlag}</span>
                        <Badge variant="outline" className="text-[10px]">{emp.region?.toUpperCase()}</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        emp.briScore >= 85 ? 'text-[#1B5E20]' : emp.briScore >= 70 ? 'text-[#1565C0]' : emp.briScore >= 55 ? 'text-[#E65100]' : 'text-[#B71C1C]'
                      }`}>{emp.briScore || '—'}</p>
                      <p className="text-[9px] text-muted-foreground">BRI</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t">
                    <SegmentBadge segment={emp.segment} />
                    <ActionBadge action={emp.recommendedAction} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {employees.length > 20 && (
            <div className="text-center mt-6">
              <Button variant="outline" onClick={() => setActiveTab("overview")}>
                View All {employees.length} Employees in Table
              </Button>
            </div>
          )}
        </main>
      )}

      {/* Tab 4: Workforce Planning */}
      {activeTab === "workforce-planning" && (
        <WorkforcePlanningTab employees={employees} stats={stats} />
      )}

      {/* Tab 5: Transformation */}
      {activeTab === "transformation" && (
        <TransformationTab employees={employees} stats={stats} briSummary={briSummary} />
      )}

      {/* Tab 7: Deliverables Hub */}
      {activeTab === "deliverables" && (
        <DeliverablesHubTab />
      )}

      {/* Tab 8: Development Pipeline */}
      {activeTab === "development-pipeline" && (
        <DevelopmentPipelineTab />
      )}

      {/* Tab 9: Manager Enablement */}
      {activeTab === "manager-enablement" && (
        <ManagerEnablementTab />
      )}

      {/* Tab 10: Compliance Status */}
      {activeTab === "compliance" && (
        <ComplianceStatusTab />
      )}

      {/* Tab 11: Programs Investment */}
      {activeTab === "programs" && (
        <ProgramsInvestmentTab />
      )}

      {/* Tab 12: Project Tracker */}
      {activeTab === "project-tracker" && (
        <ProjectTrackerTab userRole="executive" />
      )}

      {/* Footer */}
      <footer className="border-t bg-white py-4 px-6 mt-6">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Last updated: {formatTimeAgo(lastUpdated)}</span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6"
              onClick={fetchAllData}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            THCO Platform | 5-Layer Methodology™ | 17 Deliverables
          </p>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <button className="hover:text-foreground">Help</button>
            <button className="hover:text-foreground">Export</button>
            <button className="hover:text-foreground">Share</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
